// BULK Trader Revamp
import { gql, useMutation, useQuery } from '@apollo/client';
import { getLocaleString } from '@assets/i18n/config';
import type { Exchanges } from '@interfaces/financialProduct';
import BackupTableIcon from '@mui/icons-material/BackupTable'; // no icons in ovComponents
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import {
  InputAdornment,
  TextField,
} from '@ovComponents/2-component/textField/textField';
import { useGlobalToast } from '@providers/globalToastProvider';
import { dollars2cents } from '@utils/dollars2cents';
import { round } from 'lodash-es';
import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

const DISCREPANCY_THRESHOLD_PCT_PRICE = 3;
const DISCREPANCY_THRESHOLD_PCT_QUANTITY = 0;

const FETCH_BULK_TRADE_RUN = gql`
  query fetchBulkTradeRun(
    $bulkTradeRunId: ObjectID!
    $exchanges: [Exchanges!]
  ) {
    fetchBulkTradeRun(bulkTradeRunId: $bulkTradeRunId) {
      bulkTradeRun {
        id
        bulkTradeRequests(exchanges: $exchanges) {
          type
          bulkTrades {
            _id
            expectedTradePriceCents
            expectedTradePriceInCurrencyCents
            currency
            total
            actualTotal
            actualTradePriceCents
            actualTradePriceInCurrencyCents
            financialProduct {
              id
            }
          }
        }
      }
    }
  }
`;

export type AccountBulkTrade = {
  _id: string;
  expectedTradePriceCents: number;
  expectedTradePriceInCurrencyCents: number;
  expectedTradePriceInCurrency: number;
  currency: string;
  total: number;
  originalPriceInCurrencyCents: number;
  originalPriceInCurrency: number;
  originalTotal: number;
  actualTradePriceCents?: number;
  actualTradePriceInCurrencyCents?: number;
  actualTotal?: number;
};

const UPDATE_PRICE_AND_QUANTITY = gql`
  mutation calculateTradeRequestFills(
    $input: CalculateTradeRequestFillsInput!
  ) {
    calculateTradeRequestFills(input: $input) {
      success
    }
  }
`;

export const limitDecimals = (number: string): string => {
  let value = number.toString();

  const [integerPart, decimalPart] = value.split('.');

  if (decimalPart) {
    const limitedDecimal = decimalPart.slice(0, 6);
    value = `${integerPart}.${limitedDecimal}`;
  }

  return value; // should be a string to allow for a decimal point, converted to a number later
};

export const bulkPriceUpdate = (
  bulkTrades: any[],
  content: string,
  priceField: string,
  callback: (
    bulkTrades: any[],
    isHasErrors: boolean,
    notFoundSecurities: string[],
  ) => void,
) => {
  const mappedValues = content.split('\n').map((x: any) => x.split('\t'));
  let isHasErrors = false;
  const notFoundSecurities: string[] = [];
  const localBulkTrades: any[] = [...bulkTrades];

  mappedValues.forEach((line: any) => {
    if (line[0]) {
      const index = bulkTrades.findIndex((x: any) => x._id === line[0]);
      const price = round(parseFloat((line[1] ?? '').replace('$', '')), 6);
      const quantity = parseInt(line[2] ?? '', 10);

      if (index > -1) {
        if (!Number.isNaN(price)) {
          localBulkTrades[index][priceField] = price;
        }

        if (!Number.isNaN(quantity)) {
          localBulkTrades[index].total = quantity;
        }
      } else {
        notFoundSecurities.push(line[0]);
      }
    }
  });

  if (notFoundSecurities.length) {
    isHasErrors = true;
  }

  callback(localBulkTrades, isHasErrors, notFoundSecurities);
};

const UpdateTradesTable = ({
  type,
  bulkTradeRunId,
  setDiscrepancyWarning,
  exchanges = [],
  onPriceUpdate,
}: {
  type: 'BUY' | 'SELL';
  bulkTradeRunId: string;
  setDiscrepancyWarning: Dispatch<SetStateAction<boolean>>;
  exchanges?: Exchanges[];
  onPriceUpdate?: () => void;
}) => {
  const { t } = useTranslation('bulkTrader');
  const { showToast } = useGlobalToast();
  const symbolAfter = !getLocaleString().startsWith('en');

  const [bulkTrades, setBulkTrades] = useState<AccountBulkTrade[]>([]);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');

  const { data, loading } = useQuery(FETCH_BULK_TRADE_RUN, {
    variables: {
      bulkTradeRunId,
      ...(exchanges.length > 0 && { exchanges }),
    },
    fetchPolicy: 'no-cache',
  });

  const [updatePriceAndQuantity, { loading: updateLoading }] = useMutation(
    UPDATE_PRICE_AND_QUANTITY,
    {
      onCompleted: () => {
        if (onPriceUpdate) {
          onPriceUpdate();
        }
      },
    },
  );

  useEffect(() => {
    if (data) {
      const request =
        data.fetchBulkTradeRun.bulkTradeRun.bulkTradeRequests.find(
          (x: { type: string; bulkTrades: AccountBulkTrade[] }) =>
            x.type === type,
        );

      if (request?.bulkTrades) {
        setBulkTrades(
          request.bulkTrades.map((bt: AccountBulkTrade) => {
            const expectedTradePriceInCurrencyCents =
              bt.actualTradePriceInCurrencyCents ??
              bt.actualTradePriceCents ??
              bt.expectedTradePriceInCurrencyCents ??
              bt.expectedTradePriceCents;
            const expectedTradePriceInCurrency = round(
              (expectedTradePriceInCurrencyCents ?? 0) / 100,
              6,
            );

            return {
              ...bt,
              originalPriceCents: bt.expectedTradePriceCents,
              originalPriceInCurrencyCents:
                bt.expectedTradePriceInCurrencyCents,
              originalPriceInCurrency: round(
                (bt.expectedTradePriceInCurrencyCents ?? 0) / 100,
                6,
              ),
              originalTotal: bt.total,
              total: bt.actualTotal ?? bt.total,
              expectedTradePriceInCurrencyCents,
              expectedTradePriceInCurrency,
              currency: bt.currency || 'CAD',
            };
          }),
        );
      }
    }
  }, [data, type]);

  const bulkUpdate = () => {
    bulkPriceUpdate(
      bulkTrades,
      content,
      'expectedTradePriceInCurrency',
      (
        updatedBulkTrades: any[],
        isHasErrors: boolean,
        notFoundSecurities: string[],
      ) => {
        if (isHasErrors) {
          showToast({
            message: t('securitiesWereNotFound', {
              securities: notFoundSecurities.join(', '),
            }),
            severity: 'warning',
          });
        }

        if (!isHasErrors) {
          setBulkTrades(updatedBulkTrades);

          setOpen(false);
        }
      },
    );
  };

  const percentualDifference = (a: number, b: number): number => {
    try {
      return Math.abs(100 * ((a - b) / b));
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    let numWarnings = 0;
    bulkTrades.forEach((bt) => {
      if (
        percentualDifference(
          bt.expectedTradePriceInCurrency,
          bt.originalPriceInCurrency,
        ) > DISCREPANCY_THRESHOLD_PCT_PRICE
      ) {
        numWarnings++;
      }
      if (
        percentualDifference(bt.total, bt.originalTotal) >
        DISCREPANCY_THRESHOLD_PCT_QUANTITY
      ) {
        numWarnings++;
      }
    });
    setDiscrepancyWarning(numWarnings > 0);
  }, [bulkTrades, setDiscrepancyWarning]);

  const DifferenceChip = ({
    newValue,
    origValue,
    warningThresholdPct = 0,
    testId,
  }: {
    newValue: number;
    origValue: number;
    warningThresholdPct?: number;
    testId?: string;
  }): JSX.Element => {
    const delta = percentualDifference(newValue, origValue);
    const text = `${(newValue > origValue ? '+' : '-') + delta.toFixed(2)}%`;
    return (
      <Badge
        label={text}
        color={delta >= warningThresholdPct ? 'warning' : 'gray'}
        sx={{ marginLeft: '0.5em' }}
        testId={testId}
      />
    );
  };

  /**
   * use regex to prevent non numeric values
   * avoiding using "type='number'" on an input
   * @see https://mui.com/material-ui/react-text-field/#type-quot-number-quot
   */
  const updateTradePrice = (value: string, index: number): void => {
    if (!/^(\d+(\.\d*)?|)$/.test(value)) return;

    bulkTrades[index].expectedTradePriceInCurrency = limitDecimals(
      value,
    ) as any;
    setBulkTrades([...bulkTrades]);
  };

  const sanitizeTradePrice = (value: string, index: number): void => {
    bulkTrades[index].expectedTradePriceInCurrency =
      (Number(
        value.toString().replace(/^0+|(?:\.|(\..*?))0+$/gm, '$1'),
      ) as any) || 0;
    setBulkTrades([...bulkTrades]);
  };

  return (
    <Box data-testid={`bulk-trader-update-trades-table-${type.toLowerCase()}`}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid={`bulk-trader-update-trades-loading-${type.toLowerCase()}`}
        >
          <CircularProgress sx={{ m: 18 }} />
        </Box>
      ) : !bulkTrades.length ? (
        <></>
      ) : (
        <>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            flexWrap='wrap'
          >
            <Typography
              sx={{ p: 2 }}
              variant='headingSmall'
              testId={`bulk-trader-update-trades-title-${type.toLowerCase()}`}
            >
              {t(`bulk${type}Trades`)}
            </Typography>
            <IconButton
              onClick={() => setOpen(true)}
              sx={{ m: 2 }}
              testId={`bulk-trader-update-trades-bulk-import-${type.toLowerCase()}`}
            >
              <BackupTableIcon />
            </IconButton>
          </Box>
          <Table
            data-testid={`bulk-trader-update-trades-table-${type.toLowerCase()}`}
          >
            <TableHead>
              <TableRow>
                <TableHeadCell
                  testId={`bulk-trader-update-trades-header-security-${type.toLowerCase()}`}
                >
                  {t('security')}
                </TableHeadCell>
                <TableHeadCell
                  testId={`bulk-trader-update-trades-header-original-price-${type.toLowerCase()}`}
                />
                <TableHeadCell
                  testId={`bulk-trader-update-trades-header-price-${type.toLowerCase()}`}
                >
                  {t('price')}
                </TableHeadCell>
                <TableHeadCell
                  testId={`bulk-trader-update-trades-header-original-quantity-${type.toLowerCase()}`}
                />
                <TableHeadCell
                  testId={`bulk-trader-update-trades-header-quantity-${type.toLowerCase()}`}
                >
                  {t('quantity')}
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkTrades.map((line: any, index: number) => (
                <TableRow
                  key={line._id}
                  testId={`bulk-trader-update-trades-row-${type.toLowerCase()}-${index}`}
                >
                  <TableCell
                    testId={`bulk-trader-update-trades-security-${type.toLowerCase()}-${index}`}
                  >
                    <Typography weight='bold'>{line._id}</Typography>
                  </TableCell>
                  <TableCell
                    right
                    sx={{ padding: 0 }}
                    testId={`bulk-trader-update-trades-original-price-${type.toLowerCase()}-${index}`}
                  >
                    {line.expectedTradePriceInCurrency !==
                      line.originalPriceInCurrency && (
                      <Typography sx={{ color: 'text.secondary' }}>
                        {Number(line.originalPriceInCurrency).toFixed(2)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ display: 'flex', alignItems: 'center' }}
                    testId={`bulk-trader-update-trades-price-cell-${type.toLowerCase()}-${index}`}
                  >
                    <TextField
                      value={line.expectedTradePriceInCurrency}
                      onBlur={(e: any) =>
                        sanitizeTradePrice(e.target.value, index)
                      }
                      sx={{ width: '12em' }}
                      onChange={(e: any) =>
                        updateTradePrice(e.target.value, index)
                      }
                      size='small'
                      autocomplete='off'
                      testId={`bulk-trader-update-trades-price-input-${type.toLowerCase()}-${index}`}
                      InputProps={{
                        [symbolAfter ? 'endAdornment' : 'startAdornment']: (
                          <InputAdornment
                            position={symbolAfter ? 'end' : 'start'}
                          >
                            {line.currency === 'USD' ? 'US$' : '$'}
                          </InputAdornment>
                        ),
                        inputProps: {
                          min: 0,
                          step: 'any',
                          inputMode: 'decimal',
                        },
                      }}
                    />
                    {line.expectedTradePriceInCurrency !==
                      line.originalPriceInCurrency && (
                      <DifferenceChip
                        newValue={line.expectedTradePriceInCurrency}
                        origValue={line.originalPriceInCurrency}
                        warningThresholdPct={DISCREPANCY_THRESHOLD_PCT_PRICE}
                        testId={`bulk-trader-update-trades-price-difference-${type.toLowerCase()}-${index}`}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    right
                    sx={{ padding: 0 }}
                    testId={`bulk-trader-update-trades-original-quantity-${type.toLowerCase()}-${index}`}
                  >
                    {line.total !== line.originalTotal && (
                      <Typography sx={{ color: 'text.secondary' }}>
                        {line.originalTotal}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    testId={`bulk-trader-update-trades-quantity-cell-${type.toLowerCase()}-${index}`}
                  >
                    <TextField
                      value={line.total}
                      sx={{ width: '9em' }}
                      size='small'
                      type='number'
                      testId={`bulk-trader-update-trades-quantity-input-${type.toLowerCase()}-${index}`}
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                      onChange={(e: any) => {
                        bulkTrades[index].total = round(
                          parseFloat(e.target.value),
                          0,
                        );
                        setBulkTrades([...bulkTrades]);
                      }}
                    />
                    {line.total !== line.originalTotal && (
                      <DifferenceChip
                        newValue={line.total}
                        origValue={line.originalTotal}
                        warningThresholdPct={DISCREPANCY_THRESHOLD_PCT_QUANTITY}
                        testId={`bulk-trader-update-trades-quantity-difference-${type.toLowerCase()}-${index}`}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box display='flex' justifyContent='flex-end'>
            <Button
              variant='filled'
              sx={{ m: 2 }}
              disabled={updateLoading}
              onClick={() =>
                updatePriceAndQuantity({
                  variables: {
                    input: {
                      bulkTradeRunId,
                      type,
                      bulkTrades: bulkTrades.map((bulkTrade: any) => ({
                        actualPriceCents: Number(
                          dollars2cents(
                            bulkTrade.expectedTradePriceInCurrency,
                            6,
                          ),
                        ),
                        totalQuantity: bulkTrade.total,
                        financialProductId: bulkTrade.financialProduct.id,
                      })),
                    },
                  },
                })
              }
              label={t('save')}
              dataTestId={`bulk-trader-update-trades-save-${type.toLowerCase()}`}
            />
          </Box>
        </>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
        data-testid={`bulk-trader-update-trades-bulk-import-dialog-${type.toLowerCase()}`}
      >
        <DialogTitle
          data-testid={`bulk-trader-update-trades-bulk-import-title-${type.toLowerCase()}`}
        >
          {t('pasteFromExcel')}
        </DialogTitle>
        <DialogContent>
          <Typography
            testId={`bulk-trader-update-trades-bulk-import-instructions-${type.toLowerCase()}`}
          >
            {t('orderOfContent')}
          </Typography>
          <TextField
            multiline
            rows={10}
            value={content}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setContent(e.target.value)
            }
            fullWidth
            testId={`bulk-trader-update-trades-bulk-import-textarea-${type.toLowerCase()}`}
          />
          <Button
            variant='filled'
            sx={{ marginTop: 1 }}
            onClick={bulkUpdate}
            label={t('update')}
            dataTestId={`bulk-trader-update-trades-bulk-import-update-${type.toLowerCase()}`}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UpdateTradesTable;
